import LocationOnIcon from '@mui/icons-material/LocationOn';
import { debounce, Grid, Popper, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { FormikState, FormikHandlers, FormikHelpers } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetAddressesMutation } from '../../../api/autocomplete';
import { Address, AutocompletePayload } from '../../../domain/address';
import { isNonEmptyCoastAddress } from '../../../features/address/components/address-form-fields-with-autocomplete/AddressFormFieldsWithAutocomplete';

export type SmartyAddress = {
  street_line: string;
  secondary: string;
  city: string;
  state: string;
  zipcode: string;
  entries: number;
};

const createCoastAddress = (address: SmartyAddress | null): Address => {
  if (address === null) {
    return {
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
    };
  }

  return {
    streetAddress: address.street_line,
    streetAddress2: address.secondary,
    city: address.city,
    state: address.state,
    zip: address.zipcode,
  };
};

const createSmartyAddress = (address: Address): SmartyAddress => {
  return {
    street_line: address.streetAddress,
    secondary: address.streetAddress2 || '',
    city: address.city,
    state: address.state,
    zipcode: address.zip,
    entries: 1,
  };
};

const renderedOptionLabel = (option: SmartyAddress) => {
  return `${option.street_line}${
    option.secondary ? ' ' + option.secondary : ''
  }${option.entries > 1 ? ' (' + option.entries + ' entries)' : ''}, ${option.city}, ${option.state} ${option.zipcode}`;
};

const addressToSerializedString = (option: SmartyAddress) => {
  return `${option.street_line}${
    option.secondary ? ' ' + option.secondary : ''
  }${option.entries > 1 ? ' (' + option.entries + ')' : ''}, ${option.city}, ${option.state} ${option.zipcode}`;
};

const getOptionLabel = (option: SmartyAddress) => {
  return `${option.street_line}`;
};

const hasMultipleEntries = (option: SmartyAddress) => {
  return option.entries > 1;
};

type AutocompleteFieldProps = React.ComponentProps<typeof Autocomplete>;

type Props = Omit<
  AutocompleteFieldProps,
  'value' | 'error' | 'options' | 'defaultValue' | 'name' | 'renderInput' | 'onChange'
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikHandlers & FormikState<any> & FormikHelpers<any>;
  name: string;
  onValueChange?: AutocompleteFieldProps['onChange'];
  onChange?: () => void;
  label: string;
  helperText?: string;
  objectPrefix?: string;
  isPublic?: boolean;
};

const FormikAutoCompleteAddressField: React.FC<Props> = (props: Props) => {
  const [getAddresses, { data, isLoading }] = useGetAddressesMutation();
  const { formik, onChange, name, label, helperText, objectPrefix, isPublic } = props;
  const meta = formik.getFieldMeta(name);
  const initialValue = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    const address: Address = objectPrefix ? formik.values[objectPrefix].address : formik.values.address;
    if (isNonEmptyCoastAddress(address)) {
      return createSmartyAddress(address);
    }

    return null;
  }, [formik, objectPrefix]);
  const [value, setValue] = useState<SmartyAddress | null>(initialValue);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly SmartyAddress[]>([]);
  const [open, setOpen] = useState(false);
  const getAddressesDebounced = useMemo(
    () =>
      debounce(({ search, selected }: AutocompletePayload) => {
        void getAddresses({ search, selected });
      }, 500),
    [getAddresses],
  );

  const setFormikValues = useCallback(
    (address: SmartyAddress | null) => {
      void formik.setValues({
        ...formik.values,
        ...(objectPrefix && {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          [objectPrefix]: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ...formik.values[objectPrefix],
            address: createCoastAddress(address),
          },
        }),
        ...(!objectPrefix && {
          address: createCoastAddress(address),
        }),
      });
    },
    [formik, objectPrefix],
  );

  useEffect(() => {
    if (data?.suggestions && data.suggestions.length > 0) {
      setOptions([...data.suggestions]);
    }
  }, [data]);

  useEffect(() => {
    setFormikValues(value);
    setInputValue(value?.street_line || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Autocomplete
      forcePopupIcon={false}
      loading={isLoading}
      loadingText="Finding Address..."
      noOptionsText="No Addresses"
      filterOptions={(x) => x}
      filterSelectedOptions
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      open={open}
      onClose={(e, reason) => {
        // autocomplete popup state for the selectOption reason is handled in the onChange handler
        // For all other reasons, we want to close the popup, as normal
        if (reason === 'selectOption') return;
        setOpen(false);
      }}
      onChange={(e, selectedAddress: SmartyAddress | null) => {
        if (selectedAddress !== null && hasMultipleEntries(selectedAddress)) {
          const search = addressToSerializedString(selectedAddress);
          getAddressesDebounced({ search, selected: search });
        } else {
          setValue(selectedAddress);
          setOpen(false);
        }
      }}
      inputValue={inputValue}
      onInputChange={(e, newInputValue, reason) => {
        // prevent autocomplete popup from opening when component mounts ("reset")
        if (reason === 'reset') return;

        if (newInputValue.length > 0) {
          if (onChange) onChange();
          getAddressesDebounced({ search: newInputValue });
          setOpen(true);
        }

        // Because we want to allow the user to set the address manually (and not be constrained
        // by the smarty suggestions) we need to update the value
        setValue((prev) => {
          return {
            street_line: newInputValue,
            city: prev?.city || '',
            entries: prev?.entries || 1,
            state: prev?.state || '',
            zipcode: prev?.zipcode || '',
            secondary: prev?.secondary || '',
          };
        });
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label={label}
            name={name}
            error={meta.touched && !!meta.error}
            helperText={(meta.touched && meta.error) || helperText}
            fullWidth
            data-public={isPublic || undefined}
          />
        );
      }}
      PopperComponent={(popperProps) => {
        return (
          <Popper
            data-testid="address-autocomplete-popper"
            data-private={!isPublic || undefined}
            {...popperProps}
            style={{
              ...popperProps.style,
              zIndex: 1500,
            }}
          />
        );
      }}
      renderOption={(props, option) => {
        const serializedAddress = addressToSerializedString(option);
        return (
          <li {...props} key={serializedAddress} data-testid={serializedAddress}>
            <Grid container alignItems="center" mt={1} mb={1}>
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                <Typography variant="body2" component="span">
                  {renderedOptionLabel(option)}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export { FormikAutoCompleteAddressField };
